import { ResponsiveTypography } from '@doltech/ui/lib/figma/Typography/v3/ResponsiveTypography';
import { colorsV2 } from '@doltech/ui/lib/figma/colors-v2';
import { DictationWordStatusEnum, KeyboardKeyEnum } from '@doltech/utils/lib/constants';
import {
  closestDomNode,
  getNextElementWithClass,
  getPreviousElementWithClass,
} from '@doltech/utils/lib/dom';
import cl from 'classnames';
import * as React from 'react';
import AutosizeInput from 'react-input-autosize';
import styled from 'styled-components';
import { IS_ANDROID } from '@doltech/utils/lib/platform';

const Main = styled.div`
  position: relative;
  input {
    border: none;
    outline: none;
    font-style: normal;
    font-weight: 400;
    font-size: 20px;
    line-height: 28px;
    caret-color: black;
    color: inherit;
    box-sizing: unset;
    opacity: 1;
  }

  &.${DictationWordStatusEnum.CORRECT} {
    color: ${colorsV2.green100};
  }

  &.${DictationWordStatusEnum.INCORRECT} {
    color: ${colorsV2.red100};
  }

  &.${DictationWordStatusEnum.WRONG_POSITION} {
    color: ${colorsV2.yellow120};
  }

  &.disabled {
    pointer-events: none;
  }

  &.checking {
    > p {
      border-bottom: 2px solid ${colorsV2.grey20};
    }
  }
`;

const Underline = styled.div<{ $width?: number }>`
  width: ${(p) => p.$width}px;
  height: 2px;
  background-color: ${colorsV2.gray20};
  position: absolute;
  bottom: 0;
`;

interface InputDictationStudentProps {
  status?: DictationWordStatusEnum;
  value?: string;
  wordValue?: string;
  isChecking?: boolean;
  isBlank?: boolean;
  isWrongWord?: boolean;
  wordWidth?: number;
  disabled?: boolean;
  onInputChange?: (value) => void;
}

export const InputDictationStudent = (props: InputDictationStudentProps) => {
  const {
    status = DictationWordStatusEnum.READ_ONLY,
    disabled,
    value,
    wordWidth,
    onInputChange,
  } = props;
  const inputRef = React.useRef(null);

  const focusNextInput = React.useCallback(() => {
    const found = getNextElementWithClass(
      closestDomNode(inputRef.current.input, '.input-dictation-student'),
      'blank'
    );
    if (found) {
      found.querySelector('input')?.focus();
    }
  }, []);

  const focusPreviousInput = React.useCallback(() => {
    const found = getPreviousElementWithClass(
      closestDomNode(inputRef.current.input, '.input-dictation-student'),
      'blank'
    );
    if (found) {
      found.querySelector('input')?.focus();
    }
  }, []);

  if (status === DictationWordStatusEnum.READ_ONLY)
    return (
      <Main className="input-dictation-student">
        <ResponsiveTypography.Paragraph variant="regular/20-28" color="black100">
          {value}
        </ResponsiveTypography.Paragraph>
      </Main>
    );

  return (
    <Main className={cl('input-dictation-student blank', { disabled }, status)}>
      <AutosizeInput
        ref={inputRef}
        inputStyle={{
          minWidth: wordWidth,
          padding: 0,
        }}
        value={value || ''}
        onKeyDown={(event) => {
          
          switch (event.code) {
            case KeyboardKeyEnum.SPACE_KEY:
              event.preventDefault();
              focusNextInput();
              break;
            case KeyboardKeyEnum.BACKSPACE_KEY: {
              if (value?.length) return;
              focusPreviousInput();
              break;
            }
          }

          const targetValue = event?.target?.value;
          if (IS_ANDROID && targetValue?.charCodeAt(0) === 32) {
            event.preventDefault();
            focusNextInput();
            return;
          }

          if (IS_ANDROID && event?.key === KeyboardKeyEnum.BACKSPACE_KEY) {
            if (value?.length) return;
            focusPreviousInput();
            return;
          }
        }}
        onChange={(e) => {
          onInputChange?.(e.target.value.replace(/\s/g, ''));
        }}
      />
      <Underline $width={wordWidth} />
    </Main>
  );
};
